/**
 * Escape a value for interpolation into HTML text or double-quoted attributes.
 * Nullish values intentionally render as an empty string.
 */
export function escapeHTML(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Named references worth decoding. Deliberately NOT the full HTML5 table —
 * these are the ones that actually occur in this corpus's JSON-LD plus the
 * typographic characters that show up in business names. Measured over 376
 * crawled home pages: `&amp;` 44, `&quot;` 24, `&#39;` 11, `&apos;` 3.
 */
const NAMED_ENTITIES = Object.freeze({
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  copy: '©',
  reg: '®',
  trade: '™',
  hellip: '…',
  ndash: '–',
  mdash: '—',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  eacute: 'é',
  egrave: 'è',
  agrave: 'à',
  ccedil: 'ç',
  uuml: 'ü',
  ouml: 'ö',
  auml: 'ä',
  ntilde: 'ñ'
});

/**
 * Decode HTML entities in a plain-text value — the inverse of `escapeHTML`.
 *
 * ## Why this exists
 *
 * A DOM parser decodes entities on the way in, so `getAttribute('content')` and
 * `textContent` are already plain text. **JSON-LD is not** — it is JSON inside a
 * `<script>`, and `JSON.parse` decodes JSON escapes only. A site whose
 * structured data says `"name": "905 HVAC Heating &amp; Cooling"` therefore
 * yields that literal string, which matches nothing in the decoded DOM and
 * everything in the re-serialized HTML. That single mismatch made 17 templates
 * un-substitutable and hard-failed two of them for every client.
 *
 * ONE pass, so `&amp;amp;` decodes exactly one level to `&amp;` rather than
 * collapsing to `&`. Sequential `.replace()` calls would over-decode, which is
 * how an escaper becomes an injection vector in reverse.
 */
export function decodeHTMLEntities(value) {
  const text = String(value ?? '');
  if (!text.includes('&')) return text;
  return text.replace(
    /&(#\d{1,7}|#[xX][0-9a-fA-F]{1,6}|[a-zA-Z][a-zA-Z0-9]{1,9});/g,
    (match, ref) => {
      if (ref[0] === '#') {
        const code =
          ref[1] === 'x' || ref[1] === 'X'
            ? Number.parseInt(ref.slice(2), 16)
            : Number.parseInt(ref.slice(1), 10);
        if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return match;
        // Surrogates are not valid standalone scalar values; leave them encoded
        // rather than emitting a lone surrogate into a business name.
        if (code >= 0xd800 && code <= 0xdfff) return match;
        try {
          return String.fromCodePoint(code);
        } catch {
          return match;
        }
      }
      const named = NAMED_ENTITIES[ref] ?? NAMED_ENTITIES[ref.toLowerCase()];
      return named === undefined ? match : named;
    }
  );
}
